import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './Image.module.css';
import { captureAbsentImage } from '../../services/sentry';
import { useIsCaptureAbsentImageAllowed } from './hooks';

const Image = ({ src, srcAlternative, alt, fallback, className, ...props }) => {
  // TODO: srcAlternative should be temporary solution during migration
  // If your current date is 01.09.25 or later – feel free to remove this crutch :)
  const [currentSrc, setCurrentSrc] = useState(src);
  const imageRef = useRef(null);

  const isCaptureAbsentImageAllowed = useIsCaptureAbsentImageAllowed();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  // INFO: if src is changed => update current src
  useEffect(() => {
    if (currentSrc !== src && currentSrc !== srcAlternative) {
      setCurrentSrc(src);
      setIsError(false);
    }
  }, [currentSrc, srcAlternative, src]);

  const handleError = useCallback(() => {
    if (isCaptureAbsentImageAllowed && currentSrc !== srcAlternative) {
      captureAbsentImage(src);
    }

    if (srcAlternative && srcAlternative !== currentSrc) {
      setCurrentSrc(srcAlternative);
    } else {
      setIsError(true);
    }
  }, [
    setIsError,
    src,
    currentSrc,
    isCaptureAbsentImageAllowed,
    srcAlternative,
  ]);

  useEffect(() => {
    if (imageRef.current && src) {
      imageRef.current.onerror = handleError;
      imageRef.current.onload = () => setIsLoaded(true);
    }
  }, [imageRef, src, handleError]);

  if (!src && !fallback) {
    return null;
  }

  if (fallback && (isError || !src)) {
    return fallback;
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      ref={imageRef}
      onLoad={() => setIsLoaded(true)}
      onError={handleError}
      className={classNames(
        styles.image,
        isLoaded && !isError && styles.loaded,
        className
      )}
      {...props}
    />
  );
};

Image.propTypes = {
  src: PropTypes.string.isRequired,
  srcAlternative: PropTypes.string,
  alt: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.number,
  ]),
  fallback: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
};

Image.defaultProps = {
  alt: '',
  fallback: '',
};

export default Image;
